import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useApprovalNotification } from '../contexts/ApprovalNotificationContext';
import { useNotifications } from '../contexts/NotificationContext';
import { useLocation, Link } from 'react-router-dom';
import BranchSwitcher from './BranchSwitcher';
import usePermissions from '../hooks/usePermissions';
import logo from '../images/logo.png';
import smallLogo from '../images/smalllogo.png';

interface SidebarProps {
  isCollapsed: boolean;
  onToggle: () => void;
}

interface NavItem {
  id: string;
  name: string;
  icon: string;
  path: string;
  badge?: string;
}

const Sidebar: React.FC<SidebarProps> = ({ isCollapsed, onToggle }) => {
  const { user, logout } = useAuth();
  const { pendingApprovals } = useApprovalNotification();
  const { unreadCounts } = useNotifications();
  const { canAccessModule } = usePermissions();
  const location = useLocation();
  const [showAssociationsDropdown, setShowAssociationsDropdown] = useState(false);
  const navRef = useRef<HTMLElement>(null);

  // Scroll to active nav item when route changes
  useEffect(() => {
    requestAnimationFrame(() => {
      const nav = navRef.current;
      if (nav) {
        const activeItem = nav.querySelector('[data-active="true"]');
        if (activeItem) {
          activeItem.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
        }
      }
    });
  }, [location.pathname]);

  // Auto-open dropdown when navigating to an associations page
  useEffect(() => {
    const isOnAssociationsRoute = location.pathname.includes('/services') || location.pathname.includes('/university-portal');
    if (isOnAssociationsRoute && !showAssociationsDropdown) {
      setShowAssociationsDropdown(true);
    }
  }, [location.pathname]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Element;
      if (showAssociationsDropdown && !target.closest('.associations-dropdown-container')) {
        setShowAssociationsDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showAssociationsDropdown]);

  // Derive active item from current path so highlight is correct on first paint (no flicker)
  const getActiveItemFromPath = (path: string): string => {
    if (path.includes('/notifications')) return 'notifications';
    if (path.includes('/settings')) return 'settings';
    if (path.includes('/services')) return 'services';
    if (path.includes('/forms')) return 'forms';
    if (path.includes('/emails')) return 'emails';
    if (path.includes('/calls')) return 'calls';
    if (path.includes('/documents')) return 'documents';
    if (path.includes('/references')) return 'references';
    if (path.includes('/tasks')) return 'tasks';
    if (path.includes('/notes')) return 'notes';
    if (path.includes('/timeline')) return 'timeline';
    if (path.includes('/automation')) return 'automation';
    if (path.includes('/university-portal')) return 'university-portal';
    if (path.includes('/branches')) return 'branches';
    if (path.includes('/leads')) return 'leads';
    if (path.includes('/dashboard')) return 'dashboard';
    return 'dashboard';
  };
  const activeItem = getActiveItemFromPath(location.pathname);

  // Show dropdown indicator when on Services or University page (but don't force it open)
  const isOnAssociationsPage = activeItem === 'services' || activeItem === 'university-portal';
  
  const isAdmin = user?.role === 'admin' || user?.isOrgOwner;

  const allNavItems: NavItem[] = [
    { id: 'dashboard', name: 'Dashboard', icon: 'dashboard', path: '/dashboard' },
    { id: 'forms', name: 'Forms', icon: 'forms', path: '/forms' },
    { id: 'leads', name: 'Leads', icon: 'leads', path: '/leads' },
    { id: 'tasks', name: 'Tasks', icon: 'tasks', path: '/tasks' },
    { id: 'calls', name: 'Calls', icon: 'calls', path: '/calls' },
    { id: 'emails', name: 'Emails', icon: 'emails', path: '/emails' },
    { id: 'documents', name: 'Documents', icon: 'documents', path: '/documents' },
    // Associations dropdown will be added separately
    { id: 'references', name: 'References', icon: 'references', path: '/references' },
    { 
      id: 'automation', 
      name: 'Automation', 
      icon: 'automation', 
      path: '/automation',
      badge: pendingApprovals.length > 0 ? pendingApprovals.length.toString() : undefined
    },
    { id: 'settings', name: 'Settings', icon: 'settings', path: '/settings' },
  ];

  // Filter nav items based on user permissions
  const navItems = allNavItems.filter(item => {
    // Check if user has access to this module (including settings)
    return canAccessModule(item.id);
  });

  const renderIcon = (iconType: string) => {
    switch (iconType) {
      case 'dashboard':
        return (
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z" />
          </svg>
        );
      case 'leads':
        return (
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
          </svg>
        );
      case 'services':
        return (
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2 2v2m4 6h.01M5 20h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
          </svg>
        );
      case 'references':
        return (
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
          </svg>
        );
      case 'university-portal':
        return (
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 14l9-5-9-5-9 5 9 5z" />
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 14l6.16-3.422a12.083 12.083 0 01.665 6.479A11.952 11.952 0 0012 20.055a11.952 11.952 0 00-6.824-2.998 12.078 12.078 0 01.665-6.479L12 14z" />
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 14l9-5-9-5-9 5 9 5zm0 0l6.16-3.422a12.083 12.083 0 01.665 6.479A11.952 11.952 0 0012 20.055a11.952 11.952 0 00-6.824-2.998 12.078 12.078 0 01.665-6.479L12 14z" />
          </svg>
        );
      case 'tasks':
        return (
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
          </svg>
        );
      case 'notes':
        return (
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
          </svg>
        );
      case 'timeline':
        return (
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        );
      case 'automation':
        return (
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
          </svg>
        );
      case 'calls':
        return (
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
          </svg>
        );
      case 'emails':
        return (
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
          </svg>
        );
      case 'documents':
        return (
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
          </svg>
        );
      case 'forms':
        return (
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
          </svg>
        );
      case 'branches':
        return (
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
          </svg>
        );
      case 'notifications':
        return (
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
          </svg>
        );
      case 'settings':
        return (
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
          </svg>
        );
      default:
        return (
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
          </svg>
        );
    }
  };

  return (
    <div className={`bg-white shadow-lg transition-all duration-300 ${isCollapsed ? 'w-20' : 'w-64'} h-screen flex flex-col relative`}>
      {/* Collapse/Expand Toggle Button - Professional Edge-Attached Design */}
      <button
        onClick={onToggle}
        className={`
          absolute top-20 z-50 
          ${isCollapsed ? '-right-3' : '-right-3'}
          w-7 h-7 
          bg-white
          border-2 border-gray-200 
          rounded-full 
          flex items-center justify-center 
          shadow-lg hover:shadow-xl 
          hover:border-gray-300 hover:bg-gray-50
          transition-all duration-300 
          hover:scale-110 
          group
          focus:outline-none focus:ring-2 focus:ring-blue-400 focus:ring-offset-2
        `}
        title={isCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
        aria-label={isCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
      >
        <svg 
          className={`w-3.5 h-3.5 text-gray-600 group-hover:text-gray-800 transition-all duration-300 group-hover:scale-110 ${isCollapsed ? 'rotate-0' : 'rotate-180'}`} 
          fill="none" 
          stroke="currentColor" 
          viewBox="0 0 24 24"
          strokeWidth={3}
        >
          {/* Chevron pointing RIGHT when collapsed (means "expand") */}
          {/* Chevron pointing LEFT when expanded (means "collapse") */}
          <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
        </svg>
      </button>

      {/* Fixed Logo Section */}
      <div className="flex items-center justify-between p-4 border-b border-gray-200 flex-shrink-0">
        {!isCollapsed && (
          <div className="flex justify-center w-full">
            <img 
              src={logo} 
              alt="LeadsHive Logo" 
              className="rounded-lg object-cover"
              style={{ width: '12.5rem', height: '3.5rem' }}
            />
          </div>
        )}
        {isCollapsed && (
          <div className="flex justify-center w-full">
            <img 
              src={smallLogo} 
              alt="LeadsHive Logo" 
              className="w-8 h-8 rounded-lg object-cover"
            />
          </div>
        )}
      </div>

      {/* Branch Switcher */}
      {!isCollapsed && (
        <div className="p-3 border-b border-gray-200">
          <BranchSwitcher />
        </div>
      )}

      {/* Quick Notification Bell */}
      <div className="p-2 border-b border-gray-200 flex-shrink-0">
        <Link
          to="/notifications"
          className={`w-full flex items-center ${isCollapsed ? 'justify-center' : 'justify-between'} p-2 rounded-md transition-colors ${
            location.pathname.includes('/notifications') 
              ? 'bg-blue-50 text-blue-700' 
              : 'text-gray-600 hover:text-gray-800 hover:bg-gray-50'
          }`}
          title="Notifications"
        >
          <div className="flex items-center gap-2">
            <div className="relative">
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
              </svg>
              {unreadCounts.total > 0 && (
                <span className="absolute -top-1.5 -right-1.5 flex items-center justify-center h-4 min-w-[1rem] px-1 text-xs font-bold text-white bg-red-500 rounded-full">
                  {unreadCounts.total > 99 ? '99+' : unreadCounts.total}
                </span>
              )}
            </div>
            {!isCollapsed && <span className="text-sm font-medium">Notifications</span>}
          </div>
          {!isCollapsed && unreadCounts.total > 0 && (
            <span className="text-xs text-gray-500">{unreadCounts.total} new</span>
          )}
        </Link>
      </div>

      {/* Scrollable Navigation Items */}
      <nav ref={navRef} className="flex-1 px-2 py-1 space-y-0.5 overflow-y-auto scrollbar-hide" style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}>
        {navItems.map((item) => {
          // Special handling for Associations dropdown
          if (item.id === 'documents') {
            return (
              <React.Fragment key="associations-group">
                {/* Documents item */}
                <Link
                  key={item.id}
                  to={item.path}
                  data-active={activeItem === item.id}
                  className={`w-full flex items-center px-3 py-2 transition-all duration-200 ${
                    isCollapsed 
                      ? 'justify-center' 
                      : activeItem === item.id
                        ? 'bg-blue-50 text-blue-700 border-l-4 border-blue-600 rounded-lg'
                        : 'text-gray-600 hover:text-gray-800 hover:bg-gray-50 rounded-lg'
                  }`}
                  title={isCollapsed ? item.name : ''}
                >
                  <span className={`flex-shrink-0 ${isCollapsed ? `p-2 rounded-full transition-all duration-200 ${activeItem === item.id ? 'bg-blue-50 text-blue-700' : 'text-gray-600 hover:text-gray-800 hover:bg-gray-50'}` : ''}`}>
                    {renderIcon(item.icon)}
                  </span>
                  {!isCollapsed && (
                    <span className="ml-3 text-left flex-1 font-medium text-sm">{item.name}</span>
                  )}
                </Link>

                {/* Associations Dropdown */}
                <div className="associations-dropdown-container">
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      setShowAssociationsDropdown(!showAssociationsDropdown);
                    }}
                    className={`w-full flex items-center px-3 py-2 transition-all duration-200 ${
                      isCollapsed 
                        ? 'justify-center' 
                        : isOnAssociationsPage
                          ? 'bg-blue-50 text-blue-700 border-l-4 border-blue-600 rounded-lg'
                          : 'text-gray-600 hover:text-gray-800 hover:bg-gray-50 rounded-lg'
                    }`}
                    title={isCollapsed ? 'Associations' : ''}
                  >
                    <span className={`flex-shrink-0 ${isCollapsed ? `p-2 rounded-full transition-all duration-200 ${isOnAssociationsPage ? 'bg-blue-50 text-blue-700' : 'text-gray-600 hover:text-gray-800 hover:bg-gray-50'}` : ''}`}>
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
                      </svg>
                    </span>
                    {!isCollapsed && (
                      <>
                        <span className="ml-3 text-left flex-1 font-medium text-sm">Associations</span>
                        <svg className={`w-4 h-4 transition-transform ${showAssociationsDropdown ? 'rotate-90' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                        </svg>
                      </>
                    )}
                  </button>

                  {/* Dropdown Items - Expanded Sidebar */}
                  {!isCollapsed && showAssociationsDropdown && (
                    <div className="ml-4 mt-0.5 space-y-0.5">
                      <Link
                        to="/services"
                        data-active={activeItem === 'services'}
                        className={`w-full flex items-center px-2 py-1.5 pl-4 rounded-lg text-sm ${
                          activeItem === 'services'
                            ? 'bg-blue-50 text-blue-700'
                            : 'text-gray-600 hover:text-gray-800 hover:bg-gray-50'
                        }`}
                      >
                        <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2 2v2m4 6h.01M5 20h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                        </svg>
                        Services
                      </Link>
                      <Link
                        to="/university-portal"
                        data-active={activeItem === 'university-portal'}
                        className={`w-full flex items-center px-2 py-1.5 pl-4 rounded-lg text-sm ${
                          activeItem === 'university-portal'
                            ? 'bg-blue-50 text-blue-700'
                            : 'text-gray-600 hover:text-gray-800 hover:bg-gray-50'
                        }`}
                      >
                        <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 14l9-5-9-5-9 5 9 5z" />
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 14l6.16-3.422a12.083 12.083 0 01.665 6.479A11.952 11.952 0 0012 20.055a11.952 11.952 0 00-6.824-2.998 12.078 12.078 0 01.665-6.479L12 14z" />
                        </svg>
                        University
                      </Link>
                    </div>
                  )}

                  {/* Collapsed Sidebar - Sub items appear inline below */}
                  {isCollapsed && showAssociationsDropdown && (
                    <>
                      <Link
                        to="/services"
                        data-active={activeItem === 'services'}
                        onClick={() => setShowAssociationsDropdown(false)}
                        className="w-full flex items-center justify-center px-3 py-2 transition-colors"
                        title="Services"
                      >
                        <span className={`p-2 rounded-full transition-all duration-200 ${
                          activeItem === 'services'
                            ? 'bg-blue-50 text-blue-700'
                            : 'text-gray-600 hover:text-gray-800 hover:bg-gray-50'
                        }`}>
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2 2v2m4 6h.01M5 20h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                          </svg>
                        </span>
                      </Link>
                      <Link
                        to="/university-portal"
                        data-active={activeItem === 'university-portal'}
                        onClick={() => setShowAssociationsDropdown(false)}
                        className="w-full flex items-center justify-center px-3 py-2 transition-colors"
                        title="University"
                      >
                        <span className={`p-2 rounded-full transition-all duration-200 ${
                          activeItem === 'university-portal'
                            ? 'bg-blue-50 text-blue-700'
                            : 'text-gray-600 hover:text-gray-800 hover:bg-gray-50'
                        }`}>
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 14l9-5-9-5-9 5 9 5z" />
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 14l6.16-3.422a12.083 12.083 0 01.665 6.479A11.952 11.952 0 0012 20.055a11.952 11.952 0 00-6.824-2.998 12.078 12.078 0 01.665-6.479L12 14z" />
                          </svg>
                        </span>
                      </Link>
                    </>
                  )}
                </div>
              </React.Fragment>
            );
          }

          // Regular nav items
          return (
            <Link
              key={item.id}
              to={item.path}
              data-active={activeItem === item.id}
              className={`w-full flex items-center px-3 py-2 transition-all duration-200 ${
                isCollapsed 
                  ? 'justify-center' 
                  : activeItem === item.id
                    ? 'bg-blue-50 text-blue-700 border-l-4 border-blue-600 rounded-lg'
                    : 'text-gray-600 hover:text-gray-800 hover:bg-gray-50 rounded-lg'
              }`}
              title={isCollapsed ? item.name : ''}
            >
              <span className={`flex-shrink-0 ${isCollapsed ? `p-2 rounded-full transition-all duration-200 ${activeItem === item.id ? 'bg-blue-50 text-blue-700' : 'text-gray-600 hover:text-gray-800 hover:bg-gray-50'}` : ''}`}>
                {renderIcon(item.icon)}
              </span>
              {!isCollapsed && (
                <>
                  <span className="ml-3 text-left flex-1 font-medium text-sm">{item.name}</span>
                  {item.badge && (
                    <span className="ml-2 px-1.5 py-0.5 text-xs bg-red-100 text-red-600 rounded-full">
                      {item.badge}
                    </span>
                  )}
                </>
              )}
            </Link>
          );
        })}
      </nav>

      {/* Fixed User Section at Bottom */}
      <div className="p-4 border-t border-gray-200 flex-shrink-0">
        {!isCollapsed && (
          <div className="space-y-2">
            <Link
              to="/profile-settings"
              className="w-full flex items-center space-x-3 p-2 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors text-left"
              title="Profile Settings"
            >
              {user?.avatarUrl ? (
                <img
                  key={user.avatarUrl}
                  src={`${user.avatarUrl}${user.avatarUrl.includes('?') ? '&' : '?'}v=${user.updatedAt || user.id}`}
                  alt="Profile"
                  className="w-8 h-8 rounded-full object-cover"
                />
              ) : (
                <div className="w-8 h-8 bg-gray-500 rounded-full flex items-center justify-center">
                  <span className="text-white text-sm font-medium">
                    {user?.firstName?.charAt(0)}{user?.lastName?.charAt(0)}
                  </span>
                </div>
              )}
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-gray-800 truncate">
                  {user?.fullName}
                </p>
                <p className="text-xs text-gray-500 truncate">
                  {user?.isOrgOwner ? 'Owner' : 'Member'} • {user?.role}
                </p>
              </div>
            </Link>
            
            {/* Logout Button */}
            <button
              onClick={logout}
              className="w-full flex items-center space-x-3 p-2 text-gray-700 hover:bg-red-50 hover:text-red-700 rounded-lg transition-colors"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
              </svg>
              <span className="text-sm font-medium">Sign out</span>
            </button>
          </div>
        )}
        {isCollapsed && (
          <div className="space-y-2">
            <Link
              to="/profile-settings"
              className="flex justify-center w-full hover:bg-gray-100 rounded-lg p-2"
              title="Profile Settings"
            >
              {user?.avatarUrl ? (
                <img
                  key={user.avatarUrl}
                  src={`${user.avatarUrl}${user.avatarUrl.includes('?') ? '&' : '?'}v=${user.updatedAt || user.id}`}
                  alt="Profile"
                  className="w-8 h-8 rounded-full object-cover"
                />
              ) : (
                <div className="w-8 h-8 bg-gray-500 rounded-full flex items-center justify-center">
                  <span className="text-white text-sm font-medium">
                    {user?.firstName?.charAt(0)}{user?.lastName?.charAt(0)}
                  </span>
                </div>
              )}
            </Link>
            
            {/* Logout Button - Collapsed */}
            <button
              onClick={logout}
              className="w-full flex justify-center p-2 text-gray-700 hover:bg-red-50 hover:text-red-700 rounded-lg transition-colors"
              title="Sign out"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
              </svg>
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default Sidebar;
